import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { saveAs } from 'file-saver';
import { GetImageWithGidService } from '@coin/shared/auth/data-access';
import { EmployeeProfile } from '@coin/shared/org-tree/util';
import { MicrosoftTeamsService, VcfService } from '@coin/shared/core/util-helpers';
import moment from 'moment';
import { IconButtonComponent, ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';

@Component({
  selector: 'coin-org-tree-employee-profile-contact-button-bar',
  templateUrl: './contact-button-bar.component.html',
  styleUrls: ['./contact-button-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, MatIconModule, MatTooltipModule, IconButtonComponent],
  providers: [VcfService, MicrosoftTeamsService]
})
export class ContactButtonBarComponent implements OnChanges {
  private readonly dialog = inject(MatDialog);
  private readonly imageService = inject(GetImageWithGidService);
  private readonly vcfService = inject(VcfService);
  msTeamsService = inject(MicrosoftTeamsService);

  @Input({ required: true }) employeeProfile: EmployeeProfile;

  public hasValidMailAddress = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.employeeProfile) {
      this.hasValidMailAddress = this.isValidMailAddress(this.employeeProfile?.contactInfo?.email);
    }
  }

  public sendMail(): void {
    window.location.href = `mailto:${this.employeeProfile.contactInfo.email}`;
  }

  public async copyMail(event: MouseEvent): Promise<void> {
    event.preventDefault();
    await navigator.clipboard.writeText(this.employeeProfile.contactInfo.email);
    this.showEmailCopiedDialog(this.employeeProfile.contactInfo.email);
  }

  public async scheduleMeeting(): Promise<void> {
    const DEFAULT_START_DELAY = 60;
    const DEFAULT_DURATION = 30;

    this.msTeamsService.openMeetingDialog(
      `Meeting with ${this.employeeProfile.employee.firstname} ${this.employeeProfile.employee.lastname}`,
      moment().add(DEFAULT_START_DELAY, 'minutes').toDate(),
      moment()
        .add(DEFAULT_START_DELAY + DEFAULT_DURATION, 'minutes')
        .toDate(),
      [this.employeeProfile.contactInfo.email]
    );
  }

  public async downloadVcf(): Promise<void> {
    const imgSrc = await firstValueFrom(this.imageService.getImageWithStore(this.employeeProfile.employee.gid));

    const vCard = this.vcfService.generateVCard({
      firstname: this.employeeProfile.employee.firstname,
      lastname: this.employeeProfile.employee.lastname,
      middlename: this.employeeProfile.employee.middlename,
      orgCode: this.employeeProfile.employee.paOrgCode,
      email: this.employeeProfile.contactInfo.email,
      landlineNumber: this.employeeProfile.contactInfo.landline,
      mobileNumber: this.employeeProfile.contactInfo.mobile,
      imgData: imgSrc.slice(imgSrc.indexOf(',') + 1)
    });

    const blob = new Blob([vCard.toString()], { type: 'text/vcard' });
    saveAs(blob, `${this.employeeProfile.employee.firstname}_${this.employeeProfile.employee.lastname}.vcf`);
  }

  private showEmailCopiedDialog(email: string): void {
    this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        provideInput: true,
        inputReadOnly: true,
        currentInputValue: email,
        headline: 'employee-profile.actions.email-copied-header',
        translate: true,
        msg: `employee-profile.actions.email-copied-header-message`,
        confirmMsg: 'general.btnOk'
      }
    });
  }

  private isValidMailAddress(mailAddress: string): boolean {
    return !!mailAddress && mailAddress !== 'unknown@siemens.com';
  }
}
